import { FilterOutlined } from "@ant-design/icons";
import { getProjects } from "admin/rest_api";
import { Select } from "antd";
import { useQueryWithErrorHandling, useWkSelector } from "libs/react_hooks";
import { isUserAdminOrTeamManager } from "libs/utils";
import type React from "react";
import { useEffect, useState } from "react";
import { AnnotationStateFilterEnum, AnnotationTypeFilterEnum } from "viewer/constants";

type ProjectAndTypeDropdownProps = {
  selectedProjectIds: string[];
  setSelectedProjectIds: (projectIds: string[]) => void;
  selectedAnnotationType: AnnotationTypeFilterEnum;
  setSelectedAnnotationType: (type: AnnotationTypeFilterEnum) => void;
  selectedAnnotationState: string;
  setSelectedAnnotationState: (state: AnnotationStateFilterEnum) => void;

  style?: React.CSSProperties;
};

type NestedSelectOptions = {
  label: string;
  options: Array<{
    label: string;
    value: string;
  }>;
};

const ANNOTATION_TYPE_FILTERS: NestedSelectOptions = {
  label: "Filter types",
  options: [
    { label: "Tasks & Annotations", value: AnnotationTypeFilterEnum.TASKS_AND_ANNOTATIONS_KEY },
    { label: "Annotations", value: AnnotationTypeFilterEnum.ONLY_ANNOTATIONS_KEY },
    { label: "Tasks", value: AnnotationTypeFilterEnum.ONLY_TASKS_KEY },
  ],
};

const ANNOTATION_STATE_FILTERS: NestedSelectOptions = {
  label: "Filter by state",
  options: [
    { label: "Active", value: AnnotationStateFilterEnum.ACTIVE },
    { label: "Finished / Archived", value: AnnotationStateFilterEnum.FINISHED_OR_ARCHIVED },
  ],
};

function ProjectAndAnnotationTypeDropdown({
  selectedProjectIds,
  setSelectedProjectIds,
  selectedAnnotationType,
  setSelectedAnnotationType,
  selectedAnnotationState,
  setSelectedAnnotationState,
  style,
}: ProjectAndTypeDropdownProps) {
  // This state property is derived from selectedProjectIds and selectedAnnotationType.
  // It is mainly used to determine the selected items in the multiselect form item.
  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
  const [filterOptions, setFilterOptions] = useState<Array<NestedSelectOptions>>([]);
  const activeUser = useWkSelector((state) => state.activeUser);

  const { data: allProjects = [] } = useQueryWithErrorHandling({
    queryKey: ["projects"],
    enabled: activeUser != null && isUserAdminOrTeamManager(activeUser),
    queryFn: getProjects,
  });

  useEffect(() => {
    const selectedKeys =
      selectedAnnotationState !== AnnotationStateFilterEnum.ALL ? [selectedAnnotationState] : [];
    if (selectedProjectIds.length > 0) {
      setSelectedFilters([...selectedProjectIds, ...selectedKeys]);
    } else {
      setSelectedFilters([selectedAnnotationType, ...selectedKeys]);
    }
  }, [selectedProjectIds, selectedAnnotationType, selectedAnnotationState]);

  useEffect(() => {
    const projectOptions = allProjects.map((project) => {
      return {
        label: project.name,
        value: project.id,
      };
    });
    const allOptions: Array<NestedSelectOptions> = [
      ANNOTATION_TYPE_FILTERS,
      ANNOTATION_STATE_FILTERS,
    ];
    if (projectOptions.length > 0) {
      allOptions.push({ label: "Filter projects (only tasks)", options: projectOptions });
    }
    setFilterOptions(allOptions);
  }, [allProjects]);

  const setSelectedProjects = async (_prevSelection: string[], selectedValue: string) => {
    if (Object.values<string>(AnnotationStateFilterEnum).includes(selectedValue)) {
      setSelectedAnnotationState(selectedValue as AnnotationStateFilterEnum);
      return;
    }
    if (Object.values<string>(AnnotationTypeFilterEnum).includes(selectedValue)) {
      setSelectedAnnotationType(selectedValue as AnnotationTypeFilterEnum);
      setSelectedProjectIds([]);
    } else {
      setSelectedAnnotationType(AnnotationTypeFilterEnum.ONLY_TASKS_KEY);
      setSelectedProjectIds([...selectedProjectIds, selectedValue]);
    }
  };

  const onDeselect = (removedKey: string) => {
    if (Object.values<string>(AnnotationTypeFilterEnum).includes(removedKey)) {
      setSelectedAnnotationType(AnnotationTypeFilterEnum.TASKS_AND_ANNOTATIONS_KEY);
    } else if (Object.values<string>(AnnotationStateFilterEnum).includes(removedKey)) {
      setSelectedAnnotationState(AnnotationStateFilterEnum.ALL);
    } else {
      setSelectedProjectIds(selectedProjectIds.filter((projectId) => projectId !== removedKey));
    }
  };

  return (
    <Select
      className="project-and-annotation-type-dropdown"
      mode="multiple"
      placeholder="Filter tasks, annotations and projects"
      style={{ minWidth: "400px", ...style }}
      options={filterOptions}
      showSearch={{ optionFilterProp: "label" }}
      value={selectedFilters}
      popupMatchSelectWidth={false}
      maxTagCount="responsive"
      onDeselect={(removedKey: string) => onDeselect(removedKey)}
      onSelect={(newSelection: string) => setSelectedProjects(selectedFilters, newSelection)}
      prefix={<FilterOutlined />}
    />
  );
}

export default ProjectAndAnnotationTypeDropdown;
